import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TECH_LAYERS } from "@/lib/site-data";

const GAP = 84;

const CopyRow = ({ l, i, progress }) => {
  const start = 0.12 + i * 0.14;
  const opacity = useTransform(progress, [start - 0.06, start], [0.25, 1]);
  const x = useTransform(progress, [start - 0.06, start], [-16, 0]);
  return (
    <motion.div
      style={{ opacity, x }}
      className="flex items-start gap-4 border-t border-white/10 py-4"
      data-testid={`tech-layer-${i + 1}`}
    >
      <span
        className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
        style={{ background: l.color === "#151515" ? "#3a3a3a" : l.color }}
      >
        {l.n}
      </span>
      <div>
        <h4 className="font-heading text-lg font-bold text-avatar-ink">{l.title}</h4>
        <p className="text-sm leading-snug text-avatar-ink/50">{l.desc}</p>
      </div>
    </motion.div>
  );
};

const ExplodedLayer = ({ l, i, spread }) => {
  const offset = (i - 2) * GAP;
  const y = useTransform(spread, [0, 1], [0, offset]);
  const shadow = useTransform(spread, [0, 1], [0, 1]);
  const bs = useTransform(shadow, (v) => `0 ${20 * v}px ${40 * v}px rgba(0,0,0,${0.5 * v})`);
  return (
    <motion.div
      style={{ y, boxShadow: bs, zIndex: 10 - i, background: l.color === "#151515" ? "#232323" : l.color }}
      className="absolute left-1/2 top-1/2 flex h-24 w-72 -translate-x-1/2 -translate-y-1/2 items-center justify-between rounded-2xl border border-white/10 px-6"
    >
      <span className="font-heading text-3xl font-bold text-white/90">{l.n}</span>
      <span className="text-right font-heading text-sm font-semibold uppercase tracking-wide text-white/90">
        {l.title}
      </span>
    </motion.div>
  );
};

export const Technology = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const spread = useTransform(scrollYProgress, [0.05, 0.55], [0, 1]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 4]);

  return (
    <section id="technology" ref={ref} className="relative" style={{ height: "320vh" }}>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grain" />
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(229,57,53,0.08), transparent 60%)" }}
        />

        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-avatar-red">
              (5-Layer Technology)
            </p>
            <h2 className="font-heading text-4xl font-bold leading-none tracking-tighter text-avatar-ink md:text-6xl">
              Engineered<br />layer by layer.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-avatar-ink/55">
              Scroll to deconstruct the Avatar wall. Five precision-formed layers
              work in concert to protect every drop.
            </p>

            <div className="mt-10 space-y-1">
              {TECH_LAYERS.map((l, i) => (
                <CopyRow key={l.n} l={l} i={i} progress={scrollYProgress} />
              ))}
            </div>
          </div>

          <motion.div style={{ rotate }} className="relative mx-auto hidden h-[460px] w-[320px] lg:block">
            {TECH_LAYERS.map((l, i) => (
              <ExplodedLayer key={l.n} l={l} i={i} spread={spread} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
